import { Box, Flex, InlineNotification, Td, Text, Tr, useTranslation } from "@familyzone/component-library";
import React from "react";
import { useSessionStore } from "../../../storez/SessionStore";
import { School } from "../../../types/Schools";
import TableBasedPage from "../../templates/TableBasedPage";
import PurgeSchools from "./PurgeSchools";

interface SchoolsProps {
  schools: School[];
  loaded: boolean;
  withinTableChildren: JSX.Element;
  isSearching?: boolean;
  onPurgeSchools: () => void;
}

const SchoolsTable: React.FC<SchoolsProps> = ({ schools, loaded, withinTableChildren, isSearching = false, onPurgeSchools }) => {
  const { t } = useTranslation();
  const isSupportAdmin = useSessionStore.getState().isSupportAdmin();

  const breadcrumbs = [
    { title: t("Configuration"), url: "/config", isActive: false },
    { title: t("Users and Groups"), url: "/config/device/userdb", isActive: false },
    { title: t("Schools"), isActive: true },
  ];

  const columns = [
    { headerText: t("Name"), columnName: "name", sortable: true, searchable: true },
    { headerText: t("Source Type"), columnName: "sourceType", sortable: true, searchable: true },
    { headerText: t("Description"), columnName: "description", sortable: false, searchable: false },
  ];

  const tableDataMap = (school: School, index: number): JSX.Element => {
    return (
      <Tr key={index}>
        <Td data-testid="school-name">
          <Text fontSize={"sm"}>{school.name}</Text>
        </Td>
        <Td data-testid="school-source-type">
          <Text>{school.sourceType}</Text>
        </Td>
        <Td data-testid="school-description">
          <Text>{school.description}</Text>
        </Td>
      </Tr>
    );
  };

  const aboveTableChildren = (): JSX.Element => {
    return (
      <Flex mt="sp24" mr="sp24" justifyContent="end">
        {isSupportAdmin && <PurgeSchools onPurgeSchools={onPurgeSchools} isSchoolsAvailable={schools.length > 0} />}
      </Flex>
    );
  };

  const moreResultsNotification = (): JSX.Element => {
    if (!loaded || isSearching) {
      return <></>;
    }
    return (
      <Box ml="sp12" minW="300px" maxH="sp12">
        <InlineNotification
          notificationDescription="Please use search to display more schools."
          notificationTitle={`Showing you ${schools.length} school(s)`}
          status="info"
        />
      </Box>
    );
  };

  const tableChildren = (): JSX.Element => {
    return (
      <Flex>
        {withinTableChildren}
        {moreResultsNotification()}
      </Flex>
    );
  };

  return (
    <TableBasedPage
      title="Schools"
      loaded={loaded}
      breadcrumbs={breadcrumbs}
      columns={columns}
      data={schools}
      tableDataMap={tableDataMap}
      children={aboveTableChildren()}
      childrenInTableHeader={tableChildren()}
      showSearch={false}
    />
  );
};

export default SchoolsTable;
